import React, { useCallback, useState, useMemo } from 'react';
import { get } from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  DefaultButton,
  PrimaryButton,
  IconButton,
  Panel,
  PanelType,
  Dropdown,
  Text,
  TextField,
} from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidationGroup, withValidation } from '@bingads-webui-react/validation';
import {
  TARGET_TYPE,
  CONDITION_OPERATOR,
  INPUT_VALIDATION_SCHEMA,
} from '../helpers/constants';
import { extractTargetsFromViewModel, getPlaceHolderText } from '../helpers/utils';
import { TagWithTooltip } from './tag-with-tooltips';

const ValidatedInput = withValidation(TextField);

export const getUrlRulesEditorStyle = (_, { palette = {} }) => ({
  sectionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& .description': {
      marginTop: '12px',
      marginBottom: '12px',
    },
    '& .footer': {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',
    },
    '& .create-rule-button': {
      width: 'fit-content',
      padding: '6px 12px',
      marginTop: '16px',
      marginBottom: '16px',
      border: '1px solid rgba(209, 209, 209, 1)',
      borderRadius: '4px',
    },
    '& button[disabled]': {
      backgroundColor: 'transparent',
    },
  },
  rulesInputOuterContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    border: '1px solid rgba(225, 223, 221, 1)',
    borderRadius: '4px',
    padding: '16px',
    '& .add-rule-button': {
      width: 'fit-content',
      padding: '6px 12px',
      borderWidth: '0px',
    },
  },
  rulesInputInnerContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0px',
    '& .target-dropdown': {
      minWidth: '161px',
      height: '32px',
    },
    '& .conditions': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '12px',
      marginLeft: '12px',
    },
    '& .condition': {
      display: 'flex',
      flexDirection: 'row',
      gap: '12px',
      '& .operator': {
        paddingTop: '6px',
      },
    },
    '& .input-container': {
      display: 'flex',
      flexDirection: 'column',
    },
    '& .validated-input': {
      width: '231px',
      '& .ms-TextField-fieldGroup': {
        borderRadius: '4px',
      },
    },
    '& .operator-text': {
      alignSelf: 'center',
    },
    '& .error-message': {
      color: palette.redDark,
    },
  },
  rulesResultContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    borderTop: '1px solid rgba(237, 235, 233, 1)',
    '& .top-row': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginTop: '12px',
      '& .title': {
        fontSize: '18px',
        fontWeight: '600',
        lineHeight: '24px',
      },
      '& .delete-all-button': {
        width: 'fit-content',
        padding: '6px 12px',
        borderWidth: '0px',
      },
    },
    '& .result-row': {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    '& .result-items': {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '4px',
      '& .result-item': {
        alignItems: 'normal',
        backgroundColor: '#F3F2F1',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'row',
        fontWeight: 600,
        '& button': {
          color: palette.neutralSecondary,
          height: 'auto',
          '& .ms-Icon': {
            fontSize: 12,
          },
        },
        '& .tagText': {
          maxWidth: '488px',
          overflow: 'hidden',
          textWrap: 'nowrap',
          textOverflow: 'ellipsis',
          padding: '4px 0px 4px 10px',
        },
      },
    },
  },
  optionText: {
    paddingLeft: '16px',
  },
});

export const UrlRulesEditor = (props) => {
  const {
    i18n,
    urlRules,
    onChange,
    isOpen,
    onClose,
    enableSpecialCharactersValidation,
  } = props;

  const unsafeCharacterErrorMessage = i18n.getString(_TL_('Field should not contain invalid characters'));
  const doesUrlContainUnsafeCharacters = (url) => {
    const specialCharactersRegex = /[\s<>{}]/;
    return specialCharactersRegex.test(url);
  };

  const styles = useDefaultStyles(getUrlRulesEditorStyle);

  const [newAssetGroupUrlRules, setNewAssetGroupUrlRules] = useState(urlRules || []);

  const {
    urlTargets,
    pageTitleTargets,
    pageContentTargets,
    categoryTargets,
  } = useMemo(() => extractTargetsFromViewModel(newAssetGroupUrlRules), [newAssetGroupUrlRules]);

  const [selectedTargetType, setSelectedTargetType] = useState(TARGET_TYPE.Url);
  const [conditions, setConditions] = useState(['']);
  const [errorMessages, setErrorMessages] = useState([]);

  const onAddRuleButtonClick = useCallback(() => {
    setConditions([...conditions, '']);
  }, [conditions, setConditions]);

  const onCreateRuleButtonClick = useCallback(() => {
    setConditions(['']);
    if (selectedTargetType === TARGET_TYPE.Url && urlTargets.some(target => target.join(', ') === conditions.join(', '))) return;
    if (selectedTargetType === TARGET_TYPE.PageTitle && pageTitleTargets.some(target => target.join(', ') === conditions.join(', '))) return;
    if (selectedTargetType === TARGET_TYPE.PageContent && pageContentTargets.some(target => target.join(', ') === conditions.join(', '))) return;
    if (selectedTargetType === TARGET_TYPE.Category && categoryTargets.some(target => target.join(', ') === conditions.join(', '))) return;

    const rule = {};
    conditions.forEach((condition, index) => {
      const trimmedCondition = condition.trim();
      if (!trimmedCondition) return;
      rule[`TargetCondition${index + 1}`] = selectedTargetType;
      rule[`TargetConditionOperator${index + 1}`] = CONDITION_OPERATOR.Contains;
      rule[`TargetValue${index + 1}`] = trimmedCondition;
    });

    setNewAssetGroupUrlRules([...newAssetGroupUrlRules, rule]);
  }, [categoryTargets, conditions, newAssetGroupUrlRules, pageContentTargets, pageTitleTargets, selectedTargetType, urlTargets]);

  const targetTypeDropdownOptions = useMemo(() => [
    { key: TARGET_TYPE.Url, text: i18n.getString(_TL_('URL')) },
    { key: TARGET_TYPE.PageTitle, text: i18n.getString(_TL_('Page title')) },
    { key: TARGET_TYPE.PageContent, text: i18n.getString(_TL_('Page content')) },
    { key: TARGET_TYPE.Category, text: i18n.getString(_TL_('Category')) },
  ], [i18n]);

  const renderTargetTypeDropdownOptions = useCallback(option => (
    <Text className={styles.optionText}>{option.text}</Text>
  ), [styles.optionText]);

  const renderRulesInput = useCallback(() => {
    const conditionInput = conditions.map((condition, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={index} className={classnames('condition', `condition-${index + 1}`)}>
        <Text className="operator">{ index === 0 ? i18n.getString(_TL_('contains')) : i18n.getString(_TL_('And')) }</Text>
        <div className="input-container">
          <ValidatedInput
            className="validated-input"
            validationProps={{
              field: `ruleInput${index + 1}`,
              onValidate: (result) => {
                const { data, messages } = result;
                const newInput = get(data, [`ruleInput${index + 1}`], '');
                const newConditions = [...conditions];
                newConditions[index] = newInput;
                setConditions(newConditions);
                const newErrorMessages = [...errorMessages];
                enableSpecialCharactersValidation && doesUrlContainUnsafeCharacters(newInput) ? newErrorMessages[index] = [...messages, unsafeCharacterErrorMessage] : newErrorMessages[index] = messages;
                setErrorMessages(newErrorMessages);
              },
            }}
            placeholder={getPlaceHolderText(i18n, selectedTargetType)}
            value={condition}
          />
          { errorMessages[index] && errorMessages[index].map(message => (<Text className="error-message">{message}</Text>))}
        </div>
        <IconButton
          className="delete-button"
          iconProps={{ iconName: 'Delete' }}
          onClick={() => {
            if (conditions.length === 1) {
              setConditions(['']);
            } else {
              const newConditions = [...conditions];
              newConditions.splice(index, 1);
              setConditions(newConditions);
            }
          }}
        />
      </div>
    ));

    return (
      <div className={styles.rulesInputInnerContainer}>
        <Dropdown
          className="target-dropdown"
          options={targetTypeDropdownOptions}
          onRenderOption={renderTargetTypeDropdownOptions}
          selectedKey={selectedTargetType}
          onChange={(_, data) => setSelectedTargetType(data.key)}
        />
        <div className="conditions">
          { conditionInput }
        </div>
      </div>
    );
  }, [conditions, styles.rulesInputInnerContainer, targetTypeDropdownOptions, renderTargetTypeDropdownOptions, selectedTargetType, i18n, errorMessages, enableSpecialCharactersValidation, unsafeCharacterErrorMessage]);

  const removeTargetTag = useCallback((targetType, targetString) => {
    const newRules = newAssetGroupUrlRules.filter((rule) => {
      const values = [
        get(rule, 'TargetValue1', ''),
        get(rule, 'TargetValue2', ''),
        get(rule, 'TargetValue3', ''),
      ].filter(Boolean).join(', ');
      return get(rule, 'TargetCondition1', '') !== targetType || values !== targetString;
    });
    setNewAssetGroupUrlRules(newRules);
  }, [newAssetGroupUrlRules]);

  const renderTargetTags = useCallback((label, targets, targetType) => (
    <div className="result-row">
      <Text>{i18n.getString(_TL_('{{label}}'), { label })}</Text>
      <div className="result-items">
        {targets.map((target) => {
          const targetString = target.join(', ');
          return (
            <div key={targetString} className={classnames('result-item', `${targetType}-tag`)}>
              <TagWithTooltip content={targetString} />
              <IconButton
                iconProps={{ iconName: 'Cancel' }}
                onClick={() => removeTargetTag(targetType, targetString)}
              />
            </div>
          );
        })}
      </div>
    </div>
  ), [i18n, removeTargetTag]);

  const onSave = useCallback(() => {
    const newUrlRules = newAssetGroupUrlRules.filter((rule) => {
      const type = get(rule, 'TargetCondition1', '');
      return type !== TARGET_TYPE.CustomLabel;
    });
    onChange({ AssetGroupUrlTargets: newUrlRules });
    onClose();
  }, [newAssetGroupUrlRules, onChange, onClose]);

  const renderPanelFooter = useCallback(() => (
    <div className="footer">
      <PrimaryButton
        className="apply-button"
        text={i18n.getString(_TL_('Apply'))}
        onClick={onSave}
      />
      <DefaultButton
        className="cancel-button"
        text={i18n.getString(_TL_('Cancel'))}
        onClick={onClose}
      />
    </div>
  ), [i18n, onClose, onSave]);

  return (
    <>
      <Panel
        className={classnames(styles.sectionsContainer, 'url-rules-editor-panel')}
        headerText={i18n.getString(_TL_('Create URL rules'))}
        closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
        isOpen={isOpen}
        onDismiss={onClose}
        onRenderFooterContent={renderPanelFooter}
        isFooterAtBottom
        type={PanelType.medium}
      >
        <ValidationGroup
          validationProps={{
            schema: INPUT_VALIDATION_SCHEMA,
            data: { conditions },
          }}
        >
          <div className="description">
            <Text>{i18n.getString(_TL_('Enter URLs or create rules to include non-commercial pages or sections of your website.'))}</Text>
          </div>
          <div className={styles.rulesInputOuterContainer}>
            { renderRulesInput() }
            <DefaultButton
              className="add-rule-button"
              iconProps={{ iconName: 'Add' }}
              text={i18n.getString(_TL_('Add rule'))}
              onClick={onAddRuleButtonClick}
              disabled={conditions.length > 2}
            />
          </div>
          <DefaultButton
            className="create-rule-button"
            iconProps={{ iconName: 'Add' }}
            text={i18n.getString(_TL_('Create rule'))}
            onClick={onCreateRuleButtonClick}
            disabled={(conditions.length === 1 && conditions[0] === '') || errorMessages.some(messages => messages.length > 0)}
          />
          {(urlTargets.length > 0 || pageTitleTargets.length > 0 || pageContentTargets.length > 0 || categoryTargets.length > 0)
            && (
            <div className={styles.rulesResultContainer}>
              <div className="top-row">
                <Text className="title">Included URL Rules</Text>
                <DefaultButton
                  className="delete-all-button"
                  iconProps={{ iconName: 'Delete' }}
                  text={i18n.getString(_TL_('Delete all rules'))}
                  onClick={() => setNewAssetGroupUrlRules([])}
                />
              </div>
              { urlTargets.length > 0 && renderTargetTags('URL includes:', urlTargets, TARGET_TYPE.Url) }
              { pageTitleTargets.length > 0 && renderTargetTags('Page title is:', pageTitleTargets, TARGET_TYPE.PageTitle) }
              { pageContentTargets.length > 0 && renderTargetTags('Page content is:', pageContentTargets, TARGET_TYPE.PageContent) }
              { categoryTargets.length > 0 && renderTargetTags('Category is:', categoryTargets, TARGET_TYPE.Category) }
            </div>
          )}
        </ValidationGroup>
      </Panel>
    </>
  );
};

UrlRulesEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  urlRules: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.string,
    TargetCondition1: PropTypes.string,
    TargetConditionOperator1: PropTypes.string,
    TargetValue1: PropTypes.string,
    TargetCondition2: PropTypes.string,
    TargetConditionOperator2: PropTypes.string,
    TargetValue2: PropTypes.string,
    TargetCondition3: PropTypes.string,
    TargetConditionOperator3: PropTypes.string,
    TargetValue3: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  enableSpecialCharactersValidation: PropTypes.bool,
};

UrlRulesEditor.defaultProps = {
  urlRules: [],
  enableSpecialCharactersValidation: false,
};
